import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { interval, startWith, switchMap, takeWhile } from 'rxjs';

import { UI, type UiBadgeVariant, type UiTab } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import {
  RecordsOpsService,
  type BatchLine,
  type BatchProgress,
} from '../../services/records-ops.service';
import { RoleAccessService } from '../../services/role-access.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { formatHumanAmount } from '../../utils/admin-formatting';
import { RoleCode } from '../../utils/role-access';
import { TransactionHubComponent } from '../transaction-hub/transaction-hub.component';

const TERMINAL = new Set(['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED']);

function choiceValue(value: unknown): string {
  if (value && typeof value === 'object' && 'value' in value) {
    return String((value as { value?: unknown }).value ?? '');
  }
  return typeof value === 'string' ? value : '';
}

/**
 * Bulk disbursement batch: progress while it runs, its lines and the payouts it created (each linking to the payout),
 * the result file, cancel of queued lines and retry of lines that failed before a payout existed. The standard
 * transaction hub (webhooks, journals, wallet statements) stays below.
 */
@Component({
  selector: 'app-batch-disbursement-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, TransactionHubComponent, ...UI],
  template: `
    <section class="px-4 pt-3">
      <ui-card title="Processing">
        <div header-actions class="flex flex-wrap gap-2">
          <button type="button" uiButton variant="outline" size="sm" (click)="download()">Download result file</button>
          @if (canAct()) {
            <button type="button" uiButton variant="outline" size="sm" [disabled]="busy()" (click)="retry()">
              Retry failed lines
            </button>
            <button type="button" uiButton variant="danger" size="sm" [disabled]="busy()" (click)="cancel()">
              Cancel queued lines
            </button>
          }
        </div>
        @if (progress(); as p) {
          <div
            class="h-3.5 w-full overflow-hidden rounded-full bg-surface-muted"
            role="progressbar"
            [attr.aria-valuenow]="pct()"
          >
            <div class="h-full bg-brand transition-all" [style.width.%]="pct()"></div>
          </div>
          <p class="mb-1 mt-2 text-sm text-fg">
            <strong>{{ pct() }}%</strong>
            <span class="text-muted">
              — {{ p.treated_count }} / {{ p.total_count }} lines treated ·
              <span class="text-success">{{ p.success_count }} paid</span> ·
              <span class="text-danger">{{ p.failed_count }} failed</span> · {{ waiting() }} waiting
            </span>
          </p>
          @if (p.celery_task_id) {
            <p class="m-0 text-xs text-muted">
              Task <code>{{ p.celery_task_id }}</code> @if (p.celery_state) { ({{ p.celery_state }}) }
            </p>
          }
        }
        @if (message()) {
          <ui-alert class="mt-2 block" variant="info">{{ message() }}</ui-alert>
        }
      </ui-card>

      <ui-tabs
        class="mt-3"
        [tabs]="tabItems"
        [active]="tab()"
        (activeChange)="onTab($event)"
      />
      <div uiTableWrap class="mt-3">
        @if (tab() === 'lines') {
          <table uiTable>
            <thead>
              <tr>
                <th>#</th>
                <th>Account</th>
                <th>Name</th>
                <th class="text-right">Amount</th>
                <th>Status</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              @for (line of lines(); track line.id) {
                <tr>
                  <td>{{ line.line_index + 1 }}</td>
                  <td class="break-words">{{ line.destination_account }}</td>
                  <td>{{ line.destination_name }}</td>
                  <td class="text-right">{{ money(line.amount) }}</td>
                  <td>
                    <ui-badge [variant]="badge(line.status)">{{ statusText(line.status) }}</ui-badge>
                  </td>
                  <td class="text-xs text-muted">{{ line.comment || line.error_message }}</td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <table uiTable>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Account</th>
                <th class="text-right">Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              @for (payout of payouts(); track payout['id']) {
                <tr>
                  <td>
                    <a class="text-brand hover:underline" [routerLink]="payoutLink(payout)">{{
                      payout['merchant_reference'] || payout['internal_reference']
                    }}</a>
                  </td>
                  <td class="break-words">{{ payout['destination_account'] }}</td>
                  <td class="text-right">{{ money(payout['amount']) }}</td>
                  <td>
                    <ui-badge [variant]="badge(payout['status'])">{{ statusText(payout['status']) }}</ui-badge>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="4" class="text-muted">No payout created yet.</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </section>
    @if (entityConfig) {
      <app-transaction-hub [entityConfig]="entityConfig" [id]="id" />
    }
  `,
})
export class BatchDisbursementDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly ops = inject(RecordsOpsService);
  private readonly roles = inject(RoleAccessService);
  private readonly shellRoutes = inject(ShellRoutesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly progress = signal<BatchProgress | null>(null);
  readonly lines = signal<BatchLine[]>([]);
  readonly payouts = signal<Array<Record<string, unknown>>>([]);
  readonly tabItems: UiTab[] = [
    { id: 'lines', label: 'Lines' },
    { id: 'payouts', label: 'Payouts' },
  ];
  readonly tab = signal<'lines' | 'payouts'>('lines');
  readonly busy = signal(false);
  readonly message = signal('');

  readonly pct = computed(() => Math.round(this.progress()?.progress_percentage ?? 0));
  readonly waiting = computed(() => {
    const p = this.progress();
    return p ? p.total_count - p.success_count - p.failed_count : 0;
  });
  readonly canAct = computed(() => {
    const role = this.roles.effectiveRole();
    return role === RoleCode.SYSTEM_ADMIN || role === RoleCode.SYSTEM_FINANCIAL;
  });

  ngOnInit(): void {
    this.follow();
  }

  money(value: unknown): string {
    return formatHumanAmount(String(value ?? ''), '');
  }

  statusText(value: unknown): string {
    return choiceValue(value) || '—';
  }

  badge(value: unknown): UiBadgeVariant {
    const status = choiceValue(value).toUpperCase();
    if (status === 'SUCCESS' || status === 'COMPLETED') {
      return 'success';
    }
    if (status === 'FAILED' || status === 'REVERSED') {
      return 'danger';
    }
    return 'warning';
  }

  onTab(id: string): void {
    if (id === 'payouts') {
      this.openPayouts();
    } else {
      this.tab.set('lines');
    }
  }

  payoutLink(payout: Record<string, unknown>): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', 'payouts', String(payout['id']));
  }

  openPayouts(): void {
    this.tab.set('payouts');
    this.ops.batchPayouts(this.id).subscribe({ next: (page) => this.payouts.set(page.results) });
  }

  download(): void {
    this.ops.batchResult(this.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batch_${this.id}_result.csv`;
        a.click();
        URL.revokeObjectURL(url);
      },
    });
  }

  cancel(): void {
    this.act(this.ops.cancelBatch(this.id), (r) => `Queued lines cancelled: ${r.cancelled_lines}.`);
  }

  retry(): void {
    this.act(this.ops.retryBatch(this.id), (r) => `Lines sent again: ${r.requeued_lines}.`);
  }

  private act<T>(call: import('rxjs').Observable<T>, text: (result: T) => string): void {
    this.busy.set(true);
    this.message.set('');
    call.subscribe({
      next: (result) => {
        this.busy.set(false);
        this.message.set(text(result));
        this.follow();
      },
      error: (err: { error?: { detail?: string } }) => {
        this.busy.set(false);
        this.message.set(err?.error?.detail ?? 'The action was refused.');
      },
    });
  }

  /** Poll progress until every line is treated, refreshing the lines as it moves. */
  private follow(): void {
    interval(3000)
      .pipe(
        startWith(0),
        switchMap(() => this.ops.batchProgress(this.id)),
        takeWhile((p) => !(TERMINAL.has(p.status) && p.progress_percentage >= 100), true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (p) => {
          this.progress.set(p);
          this.ops.batchLines(this.id).subscribe({ next: (page) => this.lines.set(page.results) });
        },
      });
  }
}
